// Cybersource Payment Integration Service
// REST calls go through Directus; merchant/API env is for legacy token helpers only.

import axios from "axios";
import {
  billingFromRecord,
  formatBillToForCybersource,
  normalizeBillingFields,
} from "../utils/lkBillingAddress";

function mustReactEnv(name) {
  const v = process.env[name];
  if (v == null || !String(v).trim()) {
    throw new Error(`Missing required environment variable: ${name}`);
  }
  return String(v).trim();
}

const CYBERSOURCE_MERCHANT_ID = mustReactEnv("REACT_APP_CYBERSOURCE_MERCHANT_ID");
const CURRENCY = mustReactEnv("REACT_APP_CURRENCY");
const PAYMENT_API_BASE_URL = mustReactEnv("REACT_APP_PAYMENT_API_URL");
const CYBERSOURCE_UC_SCRIPT_URL = mustReactEnv("REACT_APP_CYBERSOURCE_UC_SCRIPT_URL");

/** UC billing UI: NONE hides address on gateway form; billTo is sent server-side in capture context. */
const CYBERSOURCE_UC_BILLING_TYPE = (() => {
  const raw = String(process.env.REACT_APP_CYBERSOURCE_UC_BILLING_TYPE || "NONE").trim().toUpperCase();
  if (raw === "NONE" || raw === "PARTIAL" || raw === "FULL") return raw;
  return "NONE";
})();

function reactBooleanEnv(name, defaultValue) {
  const raw = process.env[name];
  if (raw == null || !String(raw).trim()) return defaultValue;
  return String(raw).trim().toLowerCase() === "true";
}

/** Bus booking: no ship-to step on UC (default false). */
const CYBERSOURCE_UC_REQUEST_SHIPPING = reactBooleanEnv("REACT_APP_CYBERSOURCE_UC_REQUEST_SHIPPING", false);
/** Email/phone already on booking form (default false). */
const CYBERSOURCE_UC_REQUEST_EMAIL = reactBooleanEnv("REACT_APP_CYBERSOURCE_UC_REQUEST_EMAIL", false);
const CYBERSOURCE_UC_REQUEST_PHONE = reactBooleanEnv("REACT_APP_CYBERSOURCE_UC_REQUEST_PHONE", false);

export const PAYMENT_DECLINED_USER_MESSAGE =
  "Your card could not be charged. Please try another card or contact your bank.";

/** PA-safe bill-to for capture-context (Colombo defaults; not shown in UC when billingType is NONE). */
export function captureContextBillingFromRecord(record = {}) {
  return formatBillToForCybersource(normalizeBillingFields(billingFromRecord(record)));
}

export { billingFromRecord };

/** Prefer explicit first/last; fall back to splitting customerName (never "Customer"). */
export function resolveBillToNames({ firstName, lastName, customerName } = {}) {
  const explicitFirst = String(firstName ?? "").trim();
  const explicitLast = String(lastName ?? "").trim();
  if (explicitFirst && explicitLast) {
    return { firstName: explicitFirst, lastName: explicitLast };
  }
  const names = String(customerName || "").trim().split(/\s+/).filter(Boolean);
  const resolvedFirst = explicitFirst || names[0] || "Guest";
  const resolvedLast = explicitLast || names.slice(1).join(" ") || resolvedFirst;
  return { firstName: resolvedFirst, lastName: resolvedLast };
}

function normalizePhoneForCapture(phone, country = "LK") {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return "94770000000";
  if (country === "LK") {
    if (digits.startsWith("94") && digits.length >= 11) return digits;
    if (digits.startsWith("0") && digits.length >= 9) return `94${digits.slice(1)}`;
    if (digits.length === 9) return `94${digits}`;
  }
  return digits;
}

function decodeJwtPayloadBrowser(jwt) {
  try {
    const parts = String(jwt || "").split(".");
    if (parts.length < 2) return null;
    let payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    while (payload.length % 4) payload += "=";
    const json = atob(payload);
    return JSON.parse(decodeURIComponent(escape(json)));
  } catch {
    return null;
  }
}

/** Best-effort payment id/status from UC complete JWT (sent to server as backup). */
export function summarizeUcCompletePaymentSummary(completeResult) {
  const jwt = extractCompleteJwtFromUcResult(completeResult);
  if (!jwt) return null;
  const root = decodeJwtPayloadBrowser(jwt);
  if (!root) return null;
  const stack = [root];
  const seen = new Set();
  let best = null;
  while (stack.length) {
    const o = stack.pop();
    if (!o || typeof o !== "object" || seen.has(o)) continue;
    seen.add(o);
    const id = o.id ?? o.requestId ?? o.transactionId;
    const status = o.status ?? o.state;
    if (id != null && status != null) {
      const statusStr = String(status).toUpperCase();
      const isHttp = /^(?:[1-5]\d{2})$/.test(statusStr);
      const entry = {
        id: String(id),
        status: statusStr,
        approvalCode: o.processorInformation?.approvalCode ?? null,
        errorInformation: o.errorInformation ?? null,
      };
      if (!best || (!isHttp && /^(?:[1-5]\d{2})$/.test(best.status))) best = entry;
      else if (!isHttp) best = entry;
    }
    for (const v of Object.values(o)) {
      if (v && typeof v === "object") stack.push(v);
    }
  }
  return best;
}

// Generate a unique reference ID for each transaction
export const generateTransactionReference = () => {
  return `VISHMITHA_${Date.now()}_${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
};

// Create payment token for frontend
export const createPaymentToken = async (amount, reference) => {
  try {
    // This token generation would typically be done on your backend
    // For now, we'll prepare the data structure for Cybersource
    const tokenData = {
      merchantID: CYBERSOURCE_MERCHANT_ID,
      reference: reference,
      amount: Math.round(amount * 100), // Convert to cents
      currency: CURRENCY,
      timestamp: Math.floor(Date.now() / 1000),
      locale: "en",
    };

    return tokenData;
  } catch (error) {
    console.error("Error creating payment token:", error);
    throw new Error("Failed to create payment token");
  }
};

// Initialize Cybersource Payment Form
export const initializeCybersourcePaymentForm = () => {
  return new Promise((resolve, reject) => {
    if (window.microform) {
      resolve(window.microform);
    } else {
      reject(new Error("Cybersource SDK not loaded"));
    }
  });
};

// Process payment through Cybersource
export const processPayment = async (paymentDetails) => {
  try {
    const {
      amount,
      currency,
      reference,
      customerEmail,
      customerName,
      firstName: firstNameParam,
      lastName: lastNameParam,
      customerPhone,
      cardNumber,
      cardExpiry,
      cardCVV,
      bookingId,
      transientTokenJwt,
    } = paymentDetails;

    const { firstName, lastName } = resolveBillToNames({
      firstName: firstNameParam,
      lastName: lastNameParam,
      customerName,
    });

    const bill = captureContextBillingFromRecord(paymentDetails);
    const billCountry = bill.country;

    const orderInformation = {
      amountDetails: {
        totalAmount: (amount / 100).toString(),
        currency: currency,
      },
      billTo: {
        firstName,
        lastName,
        email: customerEmail,
        phoneNumber: normalizePhoneForCapture(customerPhone, billCountry),
        ...bill,
      },
    };

    /** Unified Checkout: only transient token — matches CyberSource auth-with-token example. */
    let paymentRequest;
    if (transientTokenJwt) {
      paymentRequest = {
        clientReferenceInformation: { code: reference },
        processingInformation: {
          capture: true,
          commerceIndicator: "internet",
        },
        tokenInformation: { transientTokenJwt },
        orderInformation,
      };
    } else {
      paymentRequest = {
        clientReferenceInformation: { code: reference },
        orderInformation,
        paymentInformation: {
          card: {
            number: cardNumber?.replace(/\s/g, ""),
            expirationMonth: cardExpiry?.split("/")[0],
            expirationYear: `20${cardExpiry?.split("/")[1]}`,
            securityCode: cardCVV,
          },
        },
        processingInformation: { capture: true },
      };
    }

    // Debug logs for troubleshooting
    console.log("[Cybersource] processPayment input:", paymentDetails);
    console.log("[Cybersource] paymentRequest:", paymentRequest);
    console.log("[Cybersource] bookingId:", bookingId);
    console.log("[Cybersource] reference:", reference);

    // Validation
    if (!paymentRequest || !bookingId || !reference) {
      console.error("[Cybersource] Validation failed", { paymentRequest, bookingId, reference });
      throw new Error("Missing paymentRequest, bookingId or reference");
    }

    // Make API call to your backend to process payment
    console.log("[Cybersource] Sending to backend:", {
      paymentRequest,
      bookingId,
      reference,
    });
    const response = await axios.post(
      `${PAYMENT_API_BASE_URL}/directus-extension-endpoint-cybersource/process-cybersource`,
      {
        paymentRequest,
        bookingId,
        reference,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    console.log("[Cybersource] Backend response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Payment processing error:", error);
    throw new Error(PAYMENT_DECLINED_USER_MESSAGE);
  }
};

const UC_COMPLETE_JWT_RE = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

/** Extract the payment-result JWT string from `up.complete()` (SDK should return a string). */
export function extractCompleteJwtFromUcResult(completeResult) {
  if (completeResult == null || completeResult === "") return null;
  if (typeof completeResult === "string") {
    const trimmed = completeResult.trim();
    if (UC_COMPLETE_JWT_RE.test(trimmed)) return trimmed;
    try {
      return extractCompleteJwtFromUcResult(JSON.parse(trimmed));
    } catch {
      return null;
    }
  }
  if (typeof completeResult !== "object") return null;
  for (const key of ["jwt", "token", "completeResponseJwt", "paymentResult", "completeResponse", "result", "response", "data"]) {
    const nested = completeResult[key];
    if (nested == null) continue;
    const found = extractCompleteJwtFromUcResult(nested);
    if (found) return found;
  }
  return null;
}

/** Normalize `up.complete()` return value to a JWT string or parsed object for the record endpoint. */
function normalizeCompleteResponseForApi(completeResponse) {
  if (completeResponse == null || completeResponse === "") return null;
  const jwt = extractCompleteJwtFromUcResult(completeResponse);
  if (jwt) return jwt;
  if (typeof completeResponse === "object") return completeResponse;
  return null;
}

/**
 * After Unified Checkout `unifiedPayments.complete(transientToken)` on the client, persist booking
 * (payment already processed by UC — not via /pts/v2/payments).
 */
export const recordUnifiedCheckoutPayment = async ({
  bookingId,
  reference,
  completeResponseJwt,
}) => {
  const jwt = normalizeCompleteResponseForApi(completeResponseJwt);
  if (!jwt) {
    console.error("[Cybersource] Invalid up.complete() result:", completeResponseJwt);
    throw new Error("Missing Unified Checkout complete response (expected JWT string from up.complete)");
  }
  if (typeof jwt === "string") {
    console.log("[Cybersource] Recording UC payment, JWT length:", jwt.length);
  } else {
    console.log("[Cybersource] Recording UC payment, object keys:", Object.keys(jwt));
  }

  const paymentSummary = summarizeUcCompletePaymentSummary(completeResponseJwt);

  const response = await axios.post(
    `${PAYMENT_API_BASE_URL}/directus-extension-endpoint-cybersource/unified-checkout-record-payment`,
    {
      bookingId,
      reference,
      completeResponseJwt: jwt,
      ...(paymentSummary ? { paymentSummary } : {}),
    },
    {
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
  const data = response.data;
  if (data && !data.success) {
    data.message = data.message || PAYMENT_DECLINED_USER_MESSAGE;
  }
  return data;
};

/**
 * Mark booking payment lifecycle in Directus (abandon / timeout / confirm_pending).
 * Uses the Cybersource extension (admin write) so guest tokens can still update outcomes.
 *
 * paymentStatus: abandoned | timed_out | confirm_pending
 */
export const markPaymentLifecycle = async ({
  bookingId,
  paymentStatus,
  detail = "",
  reference = "",
  releaseSeats = true,
  keepalive = false,
}) => {
  if (!bookingId || !paymentStatus) return { success: false };
  const url = `${PAYMENT_API_BASE_URL}/directus-extension-endpoint-cybersource/mark-payment-lifecycle`;
  const body = {
    bookingId,
    paymentStatus,
    detail,
    reference,
    releaseSeats,
  };
  if (keepalive && typeof fetch === "function") {
    try {
      fetch(url, {
        method: "POST",
        keepalive: true,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      return { success: true, keepalive: true };
    } catch {
      return { success: false };
    }
  }
  const response = await axios.post(url, body, {
    headers: { "Content-Type": "application/json" },
  });
  return response.data;
};

// Verify payment status
export const verifyPaymentStatus = async (reference) => {
  try {
    const response = await axios.get(`${PAYMENT_API_BASE_URL}/cybersource/status/${reference}`, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    return response.data;
  } catch (error) {
    console.error("Payment verification error:", error);
    throw new Error("Failed to verify payment status");
  }
};

// Fetch capture context for Unified Checkout initialization
export const createUnifiedCheckoutCaptureContext = async ({
  targetOrigin = window.location.origin,
  amount,
  currency = "LKR",
  customerName = "",
  firstName: firstNameParam,
  lastName: lastNameParam,
  customerEmail = "",
  customerPhone = "",
  reference = "",
  /** Optional — server applies Colombo defaults; sent in billTo, not shown in UC when billingType is NONE. */
  billingRecord = {},
  billingType = CYBERSOURCE_UC_BILLING_TYPE,
  requestShipping = CYBERSOURCE_UC_REQUEST_SHIPPING,
  requestEmail = CYBERSOURCE_UC_REQUEST_EMAIL,
  requestPhone = CYBERSOURCE_UC_REQUEST_PHONE,
} = {}) => {
  const { firstName, lastName } = resolveBillToNames({
    firstName: firstNameParam,
    lastName: lastNameParam,
    customerName,
  });
  const bill = captureContextBillingFromRecord({
    ...billingRecord,
    passenger_name: customerName,
    email: customerEmail,
    mobile_no: customerPhone,
  });
  const response = await axios.post(
    `${PAYMENT_API_BASE_URL}/directus-extension-endpoint-cybersource/capture-context`,
    {
      targetOrigin,
      amount: Number(amount || 0),
      currency,
      firstName,
      lastName,
      email: customerEmail,
      phoneNumber: normalizePhoneForCapture(customerPhone, bill.country),
      address1: bill.address1,
      buildingNumber: bill.buildingNumber,
      locality: bill.locality,
      administrativeArea: bill.administrativeArea,
      postalCode: bill.postalCode,
      country: bill.country,
      reference,
      billingType,
      requestShipping,
      requestEmail,
      requestPhone,
    },
    { headers: { "Content-Type": "application/json" } }
  );
  return response.data;
};

/** Best-effort base64url decode of the capture-context JWT payload (browser, no verification). */
function decodeCaptureContextPayload(captureContext) {
  try {
    const parts = String(captureContext || "").split(".");
    if (parts.length < 2) return null;
    let payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    while (payload.length % 4) payload += "=";
    const json = atob(payload);
    return JSON.parse(decodeURIComponent(escape(json)));
  } catch {
    return null;
  }
}

/** UC SDK URL lives inside the JWT (`ctx[0].data.clientLibrary` + `clientLibraryIntegrity`). */
export function getUnifiedCheckoutScriptInfo(captureContext) {
  const decoded = decodeCaptureContextPayload(captureContext);
  const ctxData = decoded?.ctx?.[0]?.data || decoded?.data || {};
  return {
    src: ctxData.clientLibrary || CYBERSOURCE_UC_SCRIPT_URL,
    integrity: ctxData.clientLibraryIntegrity || "",
  };
}

// Load Unified Checkout SDK script using the URL provided inside the capture context.
export const loadUnifiedCheckoutScript = (captureContext) =>
  new Promise((resolve, reject) => {
    if (window.Accept) {
      resolve(window.Accept);
      return;
    }
    const { src, integrity } = getUnifiedCheckoutScriptInfo(captureContext);
    if (!src) {
      reject(new Error("Unified Checkout script URL not found in capture context"));
      return;
    }
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.Accept));
      existing.addEventListener("error", () =>
        reject(new Error("Failed to load Unified Checkout script")),
      );
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.crossOrigin = "anonymous";
    if (integrity) script.integrity = integrity;
    script.onload = () => resolve(window.Accept);
    script.onerror = () => reject(new Error("Failed to load Unified Checkout script"));
    document.body.appendChild(script);
  });

// Handle payment callback from Cybersource
export const handlePaymentCallback = async (callbackData) => {
  try {
    console.log("Payment callback received:", callbackData);

    const {
      decision,
      reference,
      amount,
      currency,
      transactionId,
      authCode,
    } = callbackData;

    if (decision === "ACCEPT") {
      return {
        success: true,
        transactionId,
        authCode,
        reference,
        message: "Payment successful",
      };
    } else if (decision === "DECLINE") {
      return {
        success: false,
        reference,
        message: "Payment declined",
      };
    } else if (decision === "ERROR") {
      return {
        success: false,
        reference,
        message: "Payment error occurred",
      };
    }
  } catch (error) {
    console.error("Error handling payment callback:", error);
    throw error;
  }
};

// Update booking with payment information
export const updateBookingWithPayment = async (
  bookingId,
  paymentDetails
) => {
  try {
    const response = await axios.patch(
      `${PAYMENT_API_BASE_URL}/cybersource/bookings/${bookingId}/payment`,
      {
        payment_method: "cybersource_hnb",
        payment_status: paymentDetails.status,
        transaction_id: paymentDetails.transactionId,
        auth_code: paymentDetails.authCode,
        payment_date: new Date().toISOString(),
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error updating booking with payment:", error);
    throw error;
  }
};

// Format amount for display
export const formatAmount = (amount, currency = CURRENCY) => {
  return new Intl.NumberFormat("en-LK", {
    style: "currency",
    currency: currency,
  }).format(amount);
};

// Validate card details
export const validateCardDetails = (cardData) => {
  const errors = {};

  // Validate card number (basic Luhn check)
  if (!cardData.cardNumber) {
    errors.cardNumber = "Card number is required";
  } else if (!/^\d{13,19}$/.test(cardData.cardNumber.replace(/\s/g, ""))) {
    errors.cardNumber = "Invalid card number";
  }

  // Validate expiry
  if (!cardData.cardExpiry) {
    errors.cardExpiry = "Expiry date is required";
  } else if (!/^\d{2}\/\d{2}$/.test(cardData.cardExpiry)) {
    errors.cardExpiry = "Use MM/YY format";
  }

  // Validate CVV
  if (!cardData.cardCVV) {
    errors.cardCVV = "CVV is required";
  } else if (!/^\d{3,4}$/.test(cardData.cardCVV)) {
    errors.cardCVV = "Invalid CVV";
  }

  return errors;
};

export default {
  generateTransactionReference,
  createPaymentToken,
  processPayment,
  recordUnifiedCheckoutPayment,
  markPaymentLifecycle,
  verifyPaymentStatus,
  createUnifiedCheckoutCaptureContext,
  loadUnifiedCheckoutScript,
  handlePaymentCallback,
  updateBookingWithPayment,
  formatAmount,
  validateCardDetails,
  captureContextBillingFromRecord,
  CYBERSOURCE_UC_BILLING_TYPE,
  CYBERSOURCE_UC_REQUEST_SHIPPING,
  CYBERSOURCE_UC_REQUEST_EMAIL,
  CYBERSOURCE_UC_REQUEST_PHONE,
};
